import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, HeartHandshake } from 'lucide-react';
import { footerQuickLinks } from '../data/navigationData';
import { contactData } from '../data/contactData';

export default function Footer({ onOpenConsultation }) {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white relative overflow-hidden pt-14 pb-12 border-t border-neutral-800">
      {/* Decorative ambient subtle background pattern */}
      <div className="absolute top-0 right-0 -mt-24 -mr-24 w-96 h-96 rounded-full bg-[#0F766E]/15 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 -mb-24 -ml-24 w-96 h-96 rounded-full bg-[#2A9D8F]/10 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Multi-column Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-neutral-800 text-center lg:text-left">
          
          {/* Column 1: Brand & Philosophy (lg:col-span-5) */}
          <div className="lg:col-span-5 space-y-4 flex flex-col items-center lg:items-start">
            <Link to="/" className="inline-flex items-center px-3.5 py-2 rounded-xl bg-white shadow-sm hover:shadow-md transition-all duration-300 group">
              <img
                src="/umed logo.png"
                alt="UMED Rehabilitation Centre"
                className="h-12 sm:h-14 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.02]"
              />
            </Link>

            <p className="text-sm text-neutral-300 leading-relaxed max-w-md pt-2 text-center lg:text-left">
              A premier psychiatric hospital and rehabilitation centre committed to dignified clinical excellence, personalized addiction recovery, and human-centered whole-person healing.
            </p>

            <div className="pt-2 flex items-center justify-center lg:justify-start gap-2 text-xs text-[#2A9D8F] font-medium">
              <HeartHandshake className="w-4 h-4 text-[#2A9D8F]" />
              <span>Trust &bull; Safety &bull; Compassion &bull; Recovery</span>
            </div>

            {/* Social Media Links */}
            <div className="pt-3 flex flex-col items-center lg:items-start">
              <div className="text-xs font-semibold uppercase tracking-wider text-[#2A9D8F] mb-2.5">
                Connect With Us
              </div>
              <div className="flex items-center justify-center lg:justify-start gap-3">
                <a
                  href={contactData.socialLinks.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit UMED Facebook Page"
                  className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-[#2A9D8F] hover:bg-[#0F766E] text-white transition-all duration-300 group shadow-sm"
                >
                  <div className="p-1 rounded-lg bg-blue-600/20 text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium">Facebook</span>
                </a>

                <a
                  href={contactData.socialLinks.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Visit Dr. Sanad Pawar Instagram Profile"
                  className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-neutral-900 border border-neutral-800 hover:border-[#2A9D8F] hover:bg-[#0F766E] text-white transition-all duration-300 group shadow-sm"
                >
                  <div className="p-1 rounded-lg bg-white/10 text-[#2A9D8F] group-hover:bg-[#2A9D8F] group-hover:text-white transition-all">
                    <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                    </svg>
                  </div>
                  <span className="text-xs font-medium">Instagram</span>
                </a>
              </div>
            </div>
          </div>

          {/* Column 2: Quick Navigation (lg:col-span-3) */}
          <div className="lg:col-span-3 text-center lg:text-left">
            <h5 className="text-xs font-bold uppercase tracking-widest text-[#2A9D8F] mb-4">
              Navigation
            </h5>
            <ul className="space-y-2.5 text-xs text-neutral-300">
              {footerQuickLinks.map((item) => (
                <li key={item.name}>
                  <Link to={item.href} className="hover:text-white transition-colors">
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Locations & Contact (lg:col-span-4) */}
          <div className="lg:col-span-4 space-y-4 text-center lg:text-left">
            <h5 className="text-xs font-bold uppercase tracking-widest text-[#2A9D8F] mb-4">
              Our Locations & Contact
            </h5>
            
            <div className="space-y-3.5 text-xs text-neutral-300 leading-relaxed text-left">
              {/* Rehab Centre */}
              <div className="p-3.5 rounded-xl bg-neutral-900 border border-neutral-800 space-y-1.5 text-left">
                <div className="text-[11px] font-bold text-[#2A9D8F] uppercase tracking-wide text-left">
                  Rehabilitation Centre (24/7)
                </div>
                <div className="flex items-start justify-start gap-2 text-left">
                  <MapPin className="w-3.5 h-3.5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-left">
                    {contactData.address.line1}, {contactData.address.line2}, {contactData.address.city}
                  </span>
                </div>
                <div className="flex items-center justify-start gap-2 pt-0.5 text-left">
                  <Phone className="w-3.5 h-3.5 text-[#2A9D8F] flex-shrink-0" />
                  <a href={`tel:${contactData.phones[0].raw}`} className="hover:text-white transition-colors font-medium">
                    {contactData.phones[0].number}
                  </a>
                </div>
              </div>

              {/* City Clinic */}
              <div className="p-3.5 rounded-xl bg-neutral-900 border border-neutral-800 space-y-1.5 text-left">
                <div className="text-[11px] font-bold text-[#2A9D8F] uppercase tracking-wide text-left">
                  OPD Clinic & Counselling Centre
                </div>
                <div className="flex items-start justify-start gap-2 text-left">
                  <MapPin className="w-3.5 h-3.5 text-[#2A9D8F] flex-shrink-0 mt-0.5" />
                  <span className="text-left">
                    {contactData.clinicAddress.fullAddress}
                  </span>
                </div>
                <div className="flex items-center justify-start gap-2 pt-0.5 text-left">
                  <Phone className="w-3.5 h-3.5 text-[#2A9D8F] flex-shrink-0" />
                  <a href={`tel:${contactData.phones[1].raw}`} className="hover:text-white transition-colors font-medium">
                    {contactData.phones[1].number}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col lg:flex-row items-center justify-between gap-4 text-xs text-neutral-400 text-center lg:text-left">
          <p>
            &copy; {currentYear} {contactData.institutionName}. All rights reserved.
          </p>

          <div className="flex items-center gap-6">
            <span>
              Design & Developed By{' '}
              <a
                href="https://sahyadritech.in/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-[#2A9D8F] hover:text-white hover:underline transition-colors"
              >
                Sahyadri Tech
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}
